import { useCallback, useState } from 'react';

import { EditCategoryModalProps } from '~/components/common/modals/EditCategoryModal/EditCategoryModal';
import { useModal } from '~/contexts/ModalContext';
import { SharedPageTitle } from '~/pages/couples/explore/components/SearchResults/components/SharedPageTitle/SharedPageTitle';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { ALL_MARKETS } from '~/util/searchUtils';

import { PageSection } from '../../../PageSection/PageSection';
import SaveNewSearchButton from '../../../SavedSearches/SaveNewSearchButton';
import { InlineSearchBar } from '../../../SearchBar/InlineSearchBar';
import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { useVendorSearchNavigation } from '../../contexts/VendorSearchNavigationContext';
import { PartialTaxonomyNode } from '../../types/types';
import { FeaturedVendorsToolTip } from '../FeaturedVendorsToolTip/FeaturedVendorsToolTip';

import styles from './pageHeading.module.less';

const PageTitle = ({
  updateSelectedLocation,
  updateTaxonomy,
}: {
  updateSelectedLocation: (newLocation: LocationOrAllMarketsType) => void;
  updateTaxonomy: (newCategory: PartialTaxonomyNode | null) => void;
}) => {
  const { showV2PredefinedModal } = useModal();
  const [showLocationModal, setShowLocationModal] = useState(false);

  const onCategoryClick = () => {
    showV2PredefinedModal<EditCategoryModalProps>(
      'EDIT_CATEGORY_MODAL',
      {
        dialogHeadingId: 'edit-category-heading',
        triggerRef: null,
        lockBgScrolling: true,
      },
      {
        onSelect: updateTaxonomy,
      }
    );
  };

  const onLocationClick = () => {
    setShowLocationModal(true);
  };

  return (
    <SharedPageTitle
      onCategoryClick={onCategoryClick}
      onLocationClick={onLocationClick}
      onUpdateSelectedLocation={updateSelectedLocation}
      setShowLocationModal={setShowLocationModal}
      showLocationModal={showLocationModal}
    />
  );
};

const SavedSearch = () => {
  const { vendorSearch, selectedFacetOptions } = useSearchResultsPageContext();
  const { availabilityDates } = vendorSearch;

  return (
    <SaveNewSearchButton
      className={styles.saveSearch}
      vendorSearch={vendorSearch}
      selectedFacetOptions={selectedFacetOptions}
      availabilityDates={availabilityDates}
      component="link"
    />
  );
};

export const PageHeading = () => {
  const { vendorSearch } = useSearchResultsPageContext();
  const { selectedLocation, selectedTaxonomy } = vendorSearch;

  const { navigateToSrp } = useVendorSearchNavigation();

  // Navigate on change if the location is cleared
  const updateSelectedLocation = useCallback(
    (newLocation: LocationOrAllMarketsType) => {
      navigateToSrp({
        selectedLocation: newLocation,
      });
    },
    [navigateToSrp]
  );

  // Navigate on change if the category is cleared
  const updateTaxonomy = useCallback(
    (newCategory: PartialTaxonomyNode | null) => {
      navigateToSrp({
        selectedTaxonomy: newCategory,
      });
    },
    [navigateToSrp]
  );

  return (
    <PageSection className={styles.wrapper} noPadding stretchToEdge>
      <div className={styles.content}>
        <div className={styles.titleWrapper}>
          <PageTitle
            updateSelectedLocation={updateSelectedLocation}
            updateTaxonomy={updateTaxonomy}
          />
          <FeaturedVendorsToolTip />
        </div>
        <div className={styles.searchBarWrapper}>
          <InlineSearchBar
            className={styles.desktopSearchBar}
            selectedLocation={selectedLocation || ALL_MARKETS}
            allowInteractiveSearch
            updateSelectedLocation={updateSelectedLocation}
            selectedTaxonomyNode={selectedTaxonomy ?? null}
            setSelectedTaxonomyNode={updateTaxonomy}
            navigateToSrp={navigateToSrp}
            variant="white"
            reverse
            short
            searchIcon
          />
          <SavedSearch />
        </div>
      </div>
    </PageSection>
  );
};
